import {SimplexClassic as BaseSimplex} from 'services/sports/mlb/simplex/simplex-classic';
import Const from 'constants';
import Action from 'actions';
import {addRoleSuffix, getSimplexUnitName} from 'helpers/simplex-helper';
import RuleBuilder from 'services/simplex/rule-builder';
import {getPlayerService} from 'services/sports/factory';
import NumberHelper from 'helpers/number-helper';
import {calcQuantityOfReservedLineupsByExposure, countPlayerIdInLineups} from 'helpers/lineup-helper';
import {calcPercentage} from 'helpers/common-helper';

export default class SimplexClassic extends BaseSimplex {
    player({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.playerId();

        const ruleBuilder = RuleBuilder.fromExactly(sportConfig.getPlayersInTeam());

        Object.values(players).map((player) => {
            if (player.isDriver()) {
                const cptPlayerId = addRoleSuffix({prefix: player.getId(), role: Const.CAPTAIN_MODE});
                ruleBuilder.addVar(getSimplexUnitName(cptPlayerId), 1);
            }

            const playerId = addRoleSuffix({prefix: player.getId(), role: Const.DEFAULT_MODE});
            ruleBuilder.addVar(getSimplexUnitName(playerId), 1);
        });

        const result = [];

        if (ruleBuilder.isRuleCreatable()) {
            result.push(ruleBuilder.createRule());
        }
        // console.log('player',result);
        return [...result];
    }

    salary({incomingValues = {}}) {
        const {
            salaryCap,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const salaryPropsByRoles = {
            [Const.CAPTAIN_MODE]: 'captain_salary',
            [Const.DEFAULT_MODE]: 'salary',
        };

        const vars = {};
        Object.keys(playersIndexedById).map((playerId) => {
            const player = playersIndexedById[playerId];

            Object.keys(salaryPropsByRoles).map((role) => {
                if (player.isConstructor() && role === Const.CAPTAIN_MODE) {
                    return;
                }
                const simplexPlayerId = addRoleSuffix({prefix: player.getId(), role});
                vars[getSimplexUnitName(simplexPlayerId)] = player[salaryPropsByRoles[role]];
            });
        });

        const result = [];

        if (isEmpty(vars)) {
            return result;
        }

        const ruleBuilder = RuleBuilder.fromAtMost(salaryCap.getInnerMax());
        ruleBuilder.addVars(vars);
        result.push(ruleBuilder.createRule());

        if (!salaryCap.isInnerMinDefault()) {
            const ruleBuilder = RuleBuilder.fromAtLeast(salaryCap.getInnerMin());
            ruleBuilder.addVars(vars);
            result.push(ruleBuilder.createRule());
        }
        // console.log("salary\n",result);
        return [...result];
    }

    positions({incomingValues = {}}) {
        const state = Action.getState();
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = state;
        const players = preparedPlayers.groupedBy.allowedPositions();
        const positions = sportConfig.getConfigProp('positions', []);

        const result = [];

        Object.keys(positions).map((pos) => {
            const {allowedPositions, maxPlayers} = positions[pos];

            const ruleBuilder = RuleBuilder.fromExactly(maxPlayers);

            players.map((player) => {
                if (!inArray(player.position, allowedPositions)) {
                    return;
                }

                if (player.isDriver()) {
                    const playerId = addRoleSuffix({prefix: player.getId(), role: Const.CAPTAIN_MODE});
                    ruleBuilder.addVar(getSimplexUnitName(playerId), 1);
                }

                const playerId = addRoleSuffix({prefix: player.getId(), role: Const.DEFAULT_MODE});
                ruleBuilder.addVar(getSimplexUnitName(playerId), 1);
            });

            if (ruleBuilder.isRuleCreatable()) {
                result.push(ruleBuilder.createRule());
            }
        });
        // console.log("positions\n",result);
        return [...result];
    }

    team({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const teams = preparedPlayers.groupedBy.property('team', []);

        const result = [];
        Object.keys(teams).map((teamName) => {
            const players = teams[teamName];

            const ruleBuilder = RuleBuilder.fromAtMost(sportConfig.getConfigProp('maxSingleTeamPlayers'));

            players.map((player) => {
                if (player.isDriver()) {
                    const cptPlayerId = addRoleSuffix({prefix: player.getId(), role: Const.CAPTAIN_MODE});
                    ruleBuilder.addVar(getSimplexUnitName(cptPlayerId), 1);
                }

                const playerId = addRoleSuffix({prefix: player.getId(), role: Const.DEFAULT_MODE});
                ruleBuilder.addVar(getSimplexUnitName(playerId), 1);
            });

            if (ruleBuilder.isRuleCreatable()) {
                result.push(ruleBuilder.createRule());
            }
        });
        // console.log("team\n",result);
        return [...result];
    }

    captainPositions({incomingValues = {}}) {
        const {
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();

        const ruleBuilder = RuleBuilder.fromExactly(1);

        Object.values(playersIndexedById).map((player) => {
            if (player.isDriver()) {
                const cptPlayerId = addRoleSuffix({prefix: player.getId(), role: Const.CAPTAIN_MODE});
                ruleBuilder.addVar(getSimplexUnitName(cptPlayerId), 1);
            }
        });

        const result = [];

        if (ruleBuilder.isRuleCreatable()) {
            result.push(ruleBuilder.createRule());
        }
        // console.log("captainPosition\n",result);
        return [...result];
    }

    uniquePositions({incomingValues = {}}) {
        const {
            playersLock,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.playerId();

        const result = [];

        Object.values(players).map((player) => {
            const prefix = getSimplexUnitName(player.getId());
            const cptUnitId = addRoleSuffix({prefix, role: Const.CAPTAIN_MODE});
            const defUnitId = addRoleSuffix({prefix, role: Const.DEFAULT_MODE});

            const locked = [],
                notLocked = [];

            if (player.isDriver()) {
                if (playersLock.isLocked(player.getId(), Const.CAPTAIN_MODE)) {
                    locked.push(cptUnitId);
                } else {
                    notLocked.push(cptUnitId);
                }
            }

            if (playersLock.isLocked(player.getId(), Const.FLEX_MODE)) {
                locked.push(defUnitId);
            } else {
                notLocked.push(defUnitId);
            }

            if (!isEmpty(locked)) {
                const ruleBuilder = RuleBuilder.fromExactly(1);
                locked.map((simplexUnitName) => ruleBuilder.addVar(simplexUnitName, 1));
                result.push(ruleBuilder.createRule());

                if (!isEmpty(notLocked)) {
                    const ruleBuilder = RuleBuilder.fromExactly(0);
                    notLocked.map((simplexUnitName) => ruleBuilder.addVar(simplexUnitName, 1));
                    result.push(ruleBuilder.createRule());
                }
            } else if (!isEmpty(notLocked)) {
                const ruleBuilder = RuleBuilder.fromAtMost(1);
                notLocked.map((simplexUnitName) => ruleBuilder.addVar(simplexUnitName, 1));
                result.push(ruleBuilder.createRule());
            }
        });
        // console.log("uniquePositions\n",result);
        return [...result];
    }

    objectiveMax({incomingValues = {}}) {
        const {
            sportConfig,
            projectionPointsType,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.playerId();

        const Player = getPlayerService(sportConfig);
        const cptProjBy = Player.exec('definePropNameOfMode', {
            prop: projectionPointsType.getProjectionPointsType(),
            mode: Const.CAPTAIN_MODE,
        });
        const defProjBy = Player.exec('definePropNameOfMode', {
            prop: projectionPointsType.getProjectionPointsType(),
            mode: Const.DEFAULT_MODE,
        });

        const row = {};
        Object.keys(players).map((playerId) => {
            const player = players[playerId];

            if (player.isDriver()) {
                const unitId = addRoleSuffix({prefix: getSimplexUnitName(playerId), role: Const.CAPTAIN_MODE});
                row[unitId] = NumberHelper.float(player[cptProjBy], 3);
            }

            const unitId = addRoleSuffix({prefix: getSimplexUnitName(playerId), role: Const.DEFAULT_MODE});
            row[unitId] = NumberHelper.float(player[defProjBy], 3);
        });

        return {row};
    }

    createdLineups({incomingValues = {}}) {
        const {
            sportConfig,
            playersData: {preparedPlayers},
            uniquePlayersQuantity,
        } = Action.getState();
        const playersIndexedById = preparedPlayers.groupedBy.playerId();
        const playersInLineup = sportConfig.getConfigProp('playersInTeam');

        const result = [];

        this.store.get('usedPlayers', []).map((lineup) => {
            const diff = playersInLineup - uniquePlayersQuantity.getUniquePlayersQuantity();

            const ruleBuilder = RuleBuilder.fromAtMost(diff);

            lineup.map((playerId) => {
                if (!(playerId in playersIndexedById)) {
                    return;
                }

                const player = playersIndexedById[playerId];
                const prefix = getSimplexUnitName(player.getId());

                if (player.isDriver()) {
                    const unitId = addRoleSuffix({prefix, role: Const.CAPTAIN_MODE});
                    ruleBuilder.addVar(unitId, 1);
                }

                const unitId = addRoleSuffix({prefix, role: Const.DEFAULT_MODE});
                ruleBuilder.addVar(unitId, 1);
            });

            if (ruleBuilder.isRuleCreatable()) {
                result.push(ruleBuilder.createRule());
            }
        });

        return [...result];
    }

    exposureMax({incomingValues = {}}) {
        if (inArray('exposureMax', this.store.get('denyConstraints', []))) {
            return [];
        }

        const {
            exposureMode,
            lineupsQuantity,
            sportConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const modes = [Const.FLEX_MODE, Const.CAPTAIN_MODE];
        const lineups = this.store.get('lineups', []);
        const lineupsStr = lineups.join('');
        const playersForbiddenByExposureMax = [];
        const exposureMax = {};
        const calculatedLineups = this.store.get('calculatedLineupsCount', 0);
        const PlayerService = getPlayerService(sportConfig);

        function isModeApplicableToPlayer(mode, player) {
            return !(player.isConstructor() && mode === Const.CAPTAIN_MODE);
        }

        modes.map((mode) => {
            const expMaxPropName = PlayerService.exec('definePropNameOfMode', {prop: 'exposureMax', mode});
            const playersGroupedByExposureMax = preparedPlayers.groupedBy.property(expMaxPropName);

            if (exposureMode.isModeTournament()) {
                if (!calculatedLineups) {
                    return;
                }

                Object.values(playersGroupedByExposureMax).map((players) => {
                    players.map((player) => {
                        if (!isModeApplicableToPlayer(mode, player)) {
                            return;
                        }

                        const lpPlayerId = addRoleSuffix({prefix: player.playerId, role: mode});

                        if (!lineups) {
                            playersForbiddenByExposureMax.push(lpPlayerId);
                            exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                        } else {
                            const usedInLineups = countPlayerIdInLineups(lpPlayerId, lineupsStr);
                            const usedInLineupsPercentage = calcPercentage(lineups.length + 1, usedInLineups + 1);

                            if (usedInLineupsPercentage > player.getPropValueOfMode('exposureMax', mode)) {
                                playersForbiddenByExposureMax.push(lpPlayerId);
                                exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                            }
                        }
                    });
                });
            } else {
                Object.values(playersGroupedByExposureMax).map((players) => {
                    players.map((player) => {
                        if (!isModeApplicableToPlayer(mode, player)) {
                            return;
                        }

                        const lpPlayerId = addRoleSuffix({prefix: player.playerId, role: mode});
                        const usedInLineups = countPlayerIdInLineups(lpPlayerId, lineupsStr);
                        const usedInLineupsPercentage = calcPercentage(
                            lineupsQuantity.getLineupsQuantity(),
                            usedInLineups + 1,
                        );

                        if (usedInLineupsPercentage > player.getPropValueOfMode('exposureMax', mode)) {
                            playersForbiddenByExposureMax.push(lpPlayerId);
                            exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                        }
                    });
                });
            }
        });

        const playersGroupedByOverallExposureMax = preparedPlayers.groupedBy.property('overall_exposureMax');
        if (exposureMode.isModeTournament()) {
            if (!calculatedLineups) {
                return;
            }

            Object.values(playersGroupedByOverallExposureMax).map((players) => {
                players.map((player) => {
                    const lpPlayerIds = modes
                        .filter((mode) => isModeApplicableToPlayer(mode, player))
                        .map((mode) => addRoleSuffix({prefix: player.playerId, role: mode}));

                    if (!lineups) {
                        lpPlayerIds.map((lpPlayerId) => {
                            playersForbiddenByExposureMax.push(lpPlayerId);
                            exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                        });
                    } else {
                        const usedInLineups = countPlayerIdInLineups(lpPlayerIds, lineupsStr);
                        const usedInLineupsPercentage = calcPercentage(lineups.length + 1, usedInLineups + 1);

                        if (usedInLineupsPercentage > player.overall_exposureMax) {
                            lpPlayerIds.map((lpPlayerId) => {
                                playersForbiddenByExposureMax.push(lpPlayerId);
                                exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                            });
                        }
                    }
                });
            });
        } else {
            Object.values(playersGroupedByOverallExposureMax).map((players) => {
                players.map((player) => {
                    const lpPlayerIds = modes
                        .filter((mode) => isModeApplicableToPlayer(mode, player))
                        .map((mode) => addRoleSuffix({prefix: player.playerId, role: mode}));
                    const usedInLineups = countPlayerIdInLineups(lpPlayerIds, lineupsStr);
                    const usedInLineupsPercentage = calcPercentage(
                        lineupsQuantity.getLineupsQuantity(),
                        usedInLineups + 1,
                    );

                    if (usedInLineupsPercentage > player.overall_exposureMax) {
                        lpPlayerIds.map((lpPlayerId) => {
                            playersForbiddenByExposureMax.push(lpPlayerId);
                            exposureMax[getSimplexUnitName(lpPlayerId)] = 1;
                        });
                    }
                });
            });
        }

        this.store.set('playersForbiddenByExposureMax', arrayUnique(playersForbiddenByExposureMax));

        if (isEmpty(exposureMax)) {
            return [];
        }

        const ruleBuilder = RuleBuilder.fromAtMost(0);
        Object.keys(exposureMax).map((simplexUnitName) => ruleBuilder.addVar(simplexUnitName, 1));

        const result = [];
        if (ruleBuilder.isRuleCreatable()) {
            result.push(ruleBuilder.createRule());
        }
        // console.log("exposureMax\n", result)
        return [...result];
    }

    exposureMin({incomingValues: {salaryMode}}) {
        if (inArray('exposureMin', this.store.get('denyConstraints', []))) {
            return [];
        }

        const {
            sportConfig,
            lineupsQuantity,
            playersData: {preparedPlayers},
        } = Action.getState();
        const that = this;
        const availablePositions = {
            [Const.CAPTAIN_MODE]: 1,
            [Const.DEFAULT_MODE]: sportConfig.getPlayersInTeam() - 1,
        };
        const forbiddenPlayers = this.store.get('playersForbiddenByExposureMax', []);
        const result = [];
        const exposureVars = {};
        const positionsCoefficient = {};
        const diffLineupsQuantity = lineupsQuantity.getLineupsQuantity() - this.store.get('calculatedLineupsCount', 0);

        Object.keys(availablePositions).map((playerMode) => {
            const expMinPropName = getPlayerService(sportConfig).exec('definePropNameOfMode', {
                prop: 'exposureMin',
                mode: playerMode,
            });
            const playersGroupedByExposureMin = preparedPlayers.groupedBy.property(expMinPropName);

            if (isEmpty(playersGroupedByExposureMin)) {
                return;
            }

            Object.keys(playersGroupedByExposureMin).map((exp) => {
                playersGroupedByExposureMin[exp].map((player) => {
                    if (player.isExcludedByMode(playerMode, {salaryMode: salaryMode || playerMode})) {
                        return;
                    }

                    const lpPlayerId = addRoleSuffix({
                        prefix: player.playerId,
                        role: playerMode,
                    });
                    const usedInLineups = countPlayerIdInLineups(lpPlayerId, that.store.get('lineups', []).join(''));
                    const reservedLineupsQuantity = calcQuantityOfReservedLineupsByExposure(
                        lineupsQuantity.getLineupsQuantity(),
                        player.getPropValueOfMode('exposureMin', playerMode),
                    );
                    const diffReservedLineupsQuantity = reservedLineupsQuantity - usedInLineups;

                    if (diffReservedLineupsQuantity > 0) {
                        if (diffReservedLineupsQuantity >= diffLineupsQuantity) {
                            if (!inArray(lpPlayerId, forbiddenPlayers)) {
                                const ruleBuilder = RuleBuilder.fromExactly(1);
                                ruleBuilder.addVar(getSimplexUnitName(lpPlayerId), 1);
                                result.push(ruleBuilder.createRule());
                            }
                        } else if (!inArray(lpPlayerId, forbiddenPlayers)) {
                            const coefficientReservedPlaces = diffReservedLineupsQuantity / diffLineupsQuantity;
                            if (!positionsCoefficient.hasOwnProperty(playerMode)) {
                                positionsCoefficient[playerMode] = 0;
                            }
                            positionsCoefficient[playerMode] += coefficientReservedPlaces;
                            exposureVars[lpPlayerId] = 1;
                        }
                    }
                });
            });
        });

        let amountCoefficients = 0;
        Object.keys(positionsCoefficient).map((playerMode) => {
            let coef = positionsCoefficient[playerMode];
            if (coef > availablePositions[playerMode]) {
                coef = availablePositions[playerMode];
            }
            amountCoefficients += coef;
        });

        if (amountCoefficients > 0 && !isEmpty(exposureVars)) {
            amountCoefficients = Math.ceil(amountCoefficients);
            if (amountCoefficients > sportConfig.getPlayersInTeam()) {
                amountCoefficients = sportConfig.getPlayersInTeam();
            }

            const ruleBuilder = RuleBuilder.fromAtLeast(amountCoefficients);
            ruleBuilder.addVars(exposureVars);
            result.push(ruleBuilder.createRule());
        }
        // console.log("exposureMin\n",result);
        return [...result];
    }

    perTeamDriverLimits({incomingValues = {}}) {
        const {
            f1PerTeamDriverLimits,
            teamsInfo,
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersGroupedByTeams = preparedPlayers.groupedBy.property('team', []);
        const allowedTeams = f1PerTeamDriverLimits.getAllTeamNames();
        const disallowedTeams = Object.keys(teamsInfo.all()).filter((teamName) => !inArray(teamName, allowedTeams));

        const result = [];

        disallowedTeams.map((teamName) => {
            const ruleBuilder = RuleBuilder.fromAtMost(Const.F1_MAX_PER_TEAM_DRIVERS_QUANTITY);

            const players = playersGroupedByTeams[teamName] ?? [];

            players.map((player) => {
                if (player.isConstructor()) {
                    return;
                }

                const prefix = getSimplexUnitName(player.getId());
                const cptUnitId = addRoleSuffix({prefix, role: Const.CAPTAIN_MODE});
                const defUnitId = addRoleSuffix({prefix, role: Const.DEFAULT_MODE});

                ruleBuilder.addVar(cptUnitId, 1);
                ruleBuilder.addVar(defUnitId, 1);
            });

            if (ruleBuilder.isRuleCreatable()) {
                result.push(ruleBuilder.createRule());
            }
        });

        return [...result];
    }

    captainToConstructorStacking({incomingValues = {}}) {
        const {
            lineupsData: {filters},
            playersData: {preparedPlayers},
        } = Action.getState();
        const playersGroupedByTeams = preparedPlayers.groupedBy.property('team', []);

        const result = [];

        if (!filters.isStackCaptainToConstructorEnabled()) {
            return result;
        }

        const cnstrSimplexVars = {};
        Object.values(playersGroupedByTeams).map((teamPlayers) => {
            const teamCnstrSimplexVars = {},
                teamCptSimplexVars = {};

            teamPlayers.map((player) => {
                const prefix = getSimplexUnitName(player.getId());

                if (player.isConstructor()) {
                    const unitId = addRoleSuffix({prefix, role: Const.DEFAULT_MODE});
                    teamCnstrSimplexVars[unitId] = -1;
                } else {
                    const unitId = addRoleSuffix({prefix, role: Const.CAPTAIN_MODE});
                    teamCptSimplexVars[unitId] = 1;
                }
            });

            if (isEmpty(teamCnstrSimplexVars) || isEmpty(teamCptSimplexVars)) {
                return;
            }

            Object.keys(teamCnstrSimplexVars).map((unitId) => (cnstrSimplexVars[unitId] = 1));

            const ruleBuilder = RuleBuilder.fromAtLeast(0);
            ruleBuilder.addVars(teamCptSimplexVars);
            ruleBuilder.addVars(teamCnstrSimplexVars);

            result.push(ruleBuilder.createRule());
        });

        const ruleBuilder = RuleBuilder.fromExactly(1);
        ruleBuilder.addVars(cnstrSimplexVars);

        if (ruleBuilder.isRuleCreatable()) {
            result.push(ruleBuilder.createRule());
        }

        return [...result];
    }
}
